import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MoodReason } from '@peekapak/types/Mood';
import { getElementaryMoodColorFill, getSecondaryMoodColorFill } from '../MoodboardAdmin/moodboardUtilities';
import { MOODBOARD_ORDER, MOODBOARD_ORDER_6_12 } from '../../StudentDetailPanelData';
import { getChipColors, getMoodLabel, WHITE_TEXT_FAMILIES } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import type { GradeBand } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import type { SnapshotCheckIn } from '../moodboardSnapshot';
import {
  byFamily,
  byMood,
  bySource,
  countBy,
  getMonthlyTotals,
  getTopPairs,
  getUpDown,
  inSchoolYear,
} from '../moodboardSchoolYear';
import type { MonthTotals } from '../moodboardSchoolYear';
import styles from './SchoolYearSummary.module.scss';

const SOURCE_COLORS: Record<string, string> = {
  [MoodReason.FAMILY]: '#ff6319',
  [MoodReason.FRIENDS]: '#012a73',
  [MoodReason.TEACHER]: '#80379b',
  [MoodReason.CLASSMATES]: '#3ad6c5',
  [MoodReason.HOMEWORK_EXAM]: '#00afd8',
  [MoodReason.WORK]: '#f15f4a',
  [MoodReason.HEALTH]: '#69be28',
  [MoodReason.ENERGY_LEVEL]: '#fed100',
  [MoodReason.NOT_SURE]: '#828282',
};

const VIEWS = {
  mood: {
    keys: MOODBOARD_ORDER,
    keysOf: byMood,
    color: getElementaryMoodColorFill,
    darkKeys: [] as string[],
  },
  family: {
    keys: MOODBOARD_ORDER_6_12,
    keysOf: byFamily,
    color: getSecondaryMoodColorFill,
    darkKeys: WHITE_TEXT_FAMILIES,
  },
  source: {
    keys: Object.values(MoodReason),
    keysOf: bySource,
    color: (key: string) => SOURCE_COLORS[key],
    darkKeys: [MoodReason.FRIENDS, MoodReason.TEACHER] as string[],
  },
};
type View = (typeof VIEWS)[keyof typeof VIEWS];

const TYPICAL = { elementary: 'about 80% up', secondary: '50% to 60% up' };
const PIE_OPACITY = [1, 0.75, 0.55, 0.4, 0.25];

const MonthTick = ({
  x,
  y,
  payload,
  months,
}: {
  x?: number;
  y?: number;
  payload?: { index: number };
  months: MonthTotals[];
}) => {
  const month = months[payload?.index ?? 0];
  const note = month.status === 'current' ? 'so far' : month.status === 'upcoming' ? 'upcoming' : '';
  return (
    <text x={x} y={y} textAnchor='middle' className={styles.monthTick}>
      <tspan x={x} dy='1.2em'>
        {month.label}
      </tspan>
      {note && (
        <tspan x={x} dy='1.2em' className={styles.monthNote}>
          {note}
        </tspan>
      )}
    </text>
  );
};

const MonthlyChart = ({ months, view }: { months: MonthTotals[]; view: View }) => {
  const totals = months.map((month) => Object.values(month.counts).reduce((sum, count) => sum + count, 0));
  const yMax = Math.max(40, Math.ceil(Math.max(...totals) / 40) * 40);
  const data = months.map((month) => ({
    ...month.counts,
    label: month.label,
    upcoming: month.status === 'upcoming' ? yMax : 0,
  }));

  return (
    <ResponsiveContainer width='100%' height={260}>
      <BarChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke='#ebedef' />
        <XAxis
          dataKey='label'
          interval={0}
          tickLine={false}
          axisLine={{ stroke: '#d8dde3' }}
          height={40}
          tick={<MonthTick months={months} />}
        />
        <YAxis
          domain={[0, yMax]}
          ticks={[0, yMax / 4, yMax / 2, (yMax * 3) / 4, yMax]}
          tickLine={false}
          axisLine={false}
          width={32}
          fontSize={11}
        />
        <Tooltip
          cursor={{ fill: '#f9f9f9' }}
          content={({ active, label }) => {
            const index = months.findIndex((month) => month.label === label);
            if (!active || index < 0 || months[index].status === 'upcoming') return null;
            return (
              <div className={styles.tooltip}>
                {label}: {months[index].checkInCount} check-ins
              </div>
            );
          }}
        />
        {view.keys.map((key) => (
          <Bar key={key} dataKey={key} stackId='month' fill={view.color(key)} barSize={36} />
        ))}
        <Bar dataKey='upcoming' stackId='month' fill='none' stroke='#d8dde3' strokeDasharray='4 4' barSize={36} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const Legend = ({ view }: { view: View }) => (
  <div className={styles.legend}>
    {view.keys.map((key) => (
      <span key={key}>
        <i style={{ backgroundColor: view.color(key) }} />
        {key}
      </span>
    ))}
  </div>
);

const UpDown = ({ checkIns, gradeBand }: { checkIns: SnapshotCheckIn[]; gradeBand: GradeBand }) => {
  const upDown = getUpDown(checkIns, gradeBand);
  if (!upDown) return null;
  const isElementary = gradeBand === 'elementary';
  const color = isElementary ? getElementaryMoodColorFill : getSecondaryMoodColorFill;
  const downPercent = 100 - upDown.upPercent;

  return (
    <div className={styles.upDown}>
      <h3>Up and down moods this school year</h3>
      <div className={styles.upDownBar}>
        {upDown.upPercent > 0 && (
          <span style={{ width: `${upDown.upPercent}%`, backgroundColor: color('Happy') }}>{upDown.upPercent}% up</span>
        )}
        {downPercent > 0 && (
          <span
            className={styles.down}
            style={{
              width: `${downPercent}%`,
              backgroundColor: color('Angry'),
              color: isElementary ? undefined : '#fff',
            }}
          >
            {downPercent}% down
          </span>
        )}
      </div>
      <div className={styles.scale}>
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <p data-testid='up-down-typical'>
        Typical for {isElementary ? 'Elementary' : 'Secondary'} classes: <b>{TYPICAL[gradeBand]}</b>. Your class is{' '}
        <b>{upDown.comparedToTypical}</b> the typical range.
      </p>
    </div>
  );
};

const FamilyFeelings = ({ family, checkIns }: { family: string; checkIns: SnapshotCheckIn[] }) => {
  const { total, rows } = countBy(
    checkIns.filter((checkIn) => byFamily(checkIn)[0] === family),
    byMood,
  );
  const color = getSecondaryMoodColorFill(family);

  return (
    <div className={styles.familyFeelings} data-testid='family-feelings'>
      <h4>
        {family}: {total} check-ins this school year
      </h4>
      <div className={styles.pieCard}>
        <PieChart width={88} height={88}>
          <Pie data={rows} dataKey='count' innerRadius={22} outerRadius={44} stroke='#fff' isAnimationActive={false}>
            {rows.map((row, i) => (
              <Cell key={row.key} fill={color} fillOpacity={PIE_OPACITY[i % PIE_OPACITY.length]} />
            ))}
          </Pie>
        </PieChart>
        <ul>
          {rows.map((row, i) => (
            <li key={row.key}>
              <i style={{ backgroundColor: color, opacity: PIE_OPACITY[i % PIE_OPACITY.length] }} />
              <span>
                {getMoodLabel(row.key)} <small>({row.count})</small>
              </span>
              <b>{row.percent}%</b>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const YearToDate = ({
  checkIns,
  view,
  showFamilies,
}: {
  checkIns: SnapshotCheckIn[];
  view: View;
  showFamilies: boolean;
}) => {
  const [hovered, setHovered] = useState<string>();
  const [selected, setSelected] = useState<string>();
  const { rows } = countBy(checkIns, view.keysOf, view.keys);
  const openFamily = hovered ?? selected;

  return (
    <div className={styles.yearToDate}>
      <h3>Year to date</h3>
      <ul data-testid='year-to-date'>
        {rows.map((row) => {
          const content = (
            <>
              <span
                className={styles.percent}
                style={{
                  backgroundColor: view.color(row.key),
                  color: view.darkKeys.includes(row.key) ? '#fff' : undefined,
                }}
              >
                {row.percent}%
              </span>
              <span>
                {row.key} <small>({row.count})</small>
              </span>
            </>
          );
          return (
            <li key={row.key} className={styles.yearToDateRow}>
              {showFamilies ? (
                <button
                  type='button'
                  aria-expanded={openFamily === row.key}
                  onMouseEnter={() => setHovered(row.key)}
                  onMouseLeave={() => setHovered(undefined)}
                  onClick={() => setSelected(selected === row.key ? undefined : row.key)}
                  title={`Show the feelings inside ${row.key}`}
                >
                  {content}
                  <span className={styles.familyHint} aria-hidden='true' />
                </button>
              ) : (
                content
              )}
              {showFamilies && openFamily === row.key && <FamilyFeelings family={row.key} checkIns={checkIns} />}
            </li>
          );
        })}
      </ul>
      <p className={styles.note}>
        {checkIns.length} check-ins since August 1.
        {showFamilies &&
          ' Grouped by feeling family. Hover over or select a family to see the specific feelings inside it.'}
      </p>
    </div>
  );
};

const TopPairs = ({ checkIns }: { checkIns: SnapshotCheckIn[] }) => {
  const pairs = getTopPairs(checkIns);
  const max = pairs[0]?.count ?? 0;

  return (
    <section className={styles.section}>
      <h2>Feelings and sources that often go together</h2>
      <p className={styles.subtitle}>
        The feeling and source pairs your students reported most often this school year.
      </p>
      <ul className={styles.pairs} data-testid='top-pairs'>
        {pairs.map((pair) => (
          <li key={`${pair.feeling}|${pair.source}`}>
            <span className={styles.chip} style={getChipColors(pair.feeling, 'secondary')}>
              {getMoodLabel(pair.feeling)}
            </span>
            <span className={styles.with}>with</span>
            <span className={styles.sourcePill}>{pair.source}</span>
            <span className={styles.track}>
              <span style={{ width: `${(pair.count * 100) / max}%` }} />
            </span>
            <b>{pair.count}</b>
          </li>
        ))}
      </ul>
    </section>
  );
};

interface SchoolYearSummaryProps {
  gradeBand: GradeBand;
  checkIns: SnapshotCheckIn[];
}

const SchoolYearSummary = ({ gradeBand, checkIns }: SchoolYearSummaryProps) => {
  const [isBySource, setBySource] = useState(false);
  const now = Date.now();
  const yearCheckIns = inSchoolYear(checkIns, now);
  const isSecondary = gradeBand === 'secondary';
  const view = isSecondary ? (isBySource ? VIEWS.source : VIEWS.family) : VIEWS.mood;

  return (
    <>
      <section className={styles.section}>
        <div className={styles.head}>
          <div>
            <h2>School Year Summary</h2>
            <p className={styles.subtitle}>Monthly check-ins from August 1 to today</p>
          </div>
          {isSecondary && (
            <div className={styles.viewSwitch}>
              <button type='button' aria-pressed={!isBySource} onClick={() => setBySource(false)}>
                By feeling
              </button>
              <button type='button' aria-pressed={isBySource} onClick={() => setBySource(true)}>
                By source
              </button>
            </div>
          )}
        </div>
        <MonthlyChart months={getMonthlyTotals(yearCheckIns, view.keysOf, now)} view={view} />
        <Legend view={view} />
        <div className={styles.bottom}>
          <UpDown checkIns={yearCheckIns} gradeBand={gradeBand} />
          <YearToDate checkIns={yearCheckIns} view={view} showFamilies={isSecondary && !isBySource} />
        </div>
      </section>
      {isSecondary && <TopPairs checkIns={yearCheckIns} />}
    </>
  );
};

export default SchoolYearSummary;
